import { forwardRef, useEffect, useRef } from 'react';
import { Controller, useFormContext } from "react-hook-form";
import { useParams } from 'react-router-dom';
import { MDBRow, MDBCol } from "mdbreact";
import { MenuItem, Select, FormControl, Chip, Box } from "@mui/material";

import "./B2CompanyInformation.scss";

import { Button, Input, Label } from "abs-ui-components";

import { callApi } from '../../../../../services/MonitoringPlanService';
import { API_ROUTES } from '../../ApiRoutes/ApiRoutes';
import { emitToasterMsg } from '../../../../../services/toasterUtility';
import AutoSave from '../../../../../common/AutoSave/AutoSave';
import { AUTOSAVE_SUCCESS_MESSAGE, REQUIRED_FIELDS_FOR_B2 } from '../../Constants';

const B2CompanyInformation = forwardRef(({ b2CompanyInfo, refetchMonitoringData,vesselMstId, b2CompanyInfoValues }, ref) => {
  const {
    control,
    formState: { errors, isDirty },
    trigger,
    reset,
    getValues,
  } = useFormContext();

  
  const manualSaveTriggered = useRef(false);
  const handleBlur = async (fieldName) => {
    await trigger(fieldName);
  };

  const companyNatureOptions = b2CompanyInfoValues || [];

  useEffect(() => {
    if (b2CompanyInfo) {
      reset({
        companyName: b2CompanyInfo?.companyName || "",
        companyNatures: b2CompanyInfo?.companyNatures || [],
        companyImoNumber: b2CompanyInfo?.companyImoNumber || "",
        countryOfRegistration: b2CompanyInfo?.countryOfRegistration || "",
        taxNumber: b2CompanyInfo?.taxNumber || "",
        contactPerson: b2CompanyInfo?.contactPerson || "",
        businessAddress: b2CompanyInfo?.businessAddress || "",
        city: b2CompanyInfo?.city || "",
        stateProvinceRegion: b2CompanyInfo?.stateProvinceRegion || "",
        zipCode: b2CompanyInfo?.zipCode || "",
        businessTelephone: b2CompanyInfo?.businessTelephone || "",
        businessEmail: b2CompanyInfo?.businessEmail || "",
        country: b2CompanyInfo?.country || ""
      });
    }
  }, [b2CompanyInfo, reset]);

  AutoSave(ref, REQUIRED_FIELDS_FOR_B2, async () => {
    if (!isDirty) return;
    const formValues = getValues();
    await submitData(formValues, true);
  });

  const submitData = async (data, isAuto = false) => {
    const payload = {
      companyName: data?.companyName || "",
      companyNatures: data?.companyNatures || [],
      companyImoNumber: data?.companyImoNumber || "",
      countryOfRegistration: data?.countryOfRegistration || "",
      taxNumber: data?.taxNumber || "",
      contactPerson: data?.contactPerson || "",
      businessAddress: data?.businessAddress || "",
      city: data?.city || "",
      stateProvinceRegion: data?.stateProvinceRegion || "",
      zipCode: data?.zipCode || "",
      businessTelephone: data?.businessTelephone || "",
      businessEmail: data?.businessEmail || "",
      country: data?.country || ""
    };
    try {
      const response = await callApi({
        method: "POST",
        url: API_ROUTES.B2(vesselMstId),
        data: payload,
      });
      if (response?.isSuccess) {
        emitToasterMsg("success", "Table B.2", isAuto ? AUTOSAVE_SUCCESS_MESSAGE : "Created successfully");
        reset(getValues());
        if (typeof refetchMonitoringData === "function") {
          await refetchMonitoringData();
        }
      } else {
        emitToasterMsg("error", "Table B.2", response?.errorMessage || "Save failed");
      }
    } catch (error) {
      emitToasterMsg("error", "Table B.2", error?.errorMessage);
    }
  };

  return (
    <div className="form-section-b2 mt-5">
      <MDBRow className="align-items-center mb-2">
        <MDBCol md="4">
          <Label name="Name of the company" bold required />
          <Controller
            name="companyName"
            control={control}
            rules={{ required: "Field is required" }}
            render={({ field }) => (
              <>
                <Input
                  id="b2.nameOFTheCompany"
                  autoFocus={false}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={() => handleBlur(field.name)}
                  className="form-input"
                  maxLength={255}
                  required
                />
                <div className="error-msg-b2ship">
                  {errors?.companyName?.message || <>&nbsp;</>}
                </div>
              </>
            )}
          />
        </MDBCol>
        <MDBCol md="4">
          <Label name="Nature of the company" bold required />
          <Controller
            name="companyNatures"
            control={control}
            rules={{ required: "Field is required" }}
            render={({ field }) => (
              <>
                <FormControl fullWidth size="small">
                  <Select
                    className="autocomplete-input"
                    labelId="b2.company-nature-label"
                    multiple
                    value={field.value || []}
                    onChange={(event) => {
                      field.onChange(event);
                      trigger("companyNatures"); // validate immediately
                    }}
                    onBlur={() => trigger("companyNatures")}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected?.map((val) => {
                          const item = companyNatureOptions.find((opt) => opt?.code === val);

                          return (
                            <Chip
                              key={val}
                              label={item?.name || val}
                              onMouseDown={(event) => {
                                event.stopPropagation();
                              }}
                              onDelete={() => {
                                const newValue = selected.filter((v) => v !== val);
                                field.onChange(newValue);
                                trigger("companyNatures");
                              }}
                            />
                          );
                        })}
                      </Box>
                    )}
                    MenuProps={{
                      disableScrollLock: true,
                      PaperProps: {
                        sx: {
                          maxHeight: 250,
                          mt: 1,
                          boxShadow: 3,
                        },
                      },
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      getContentAnchorEl: null,
                    }}
                  >
                    {companyNatureOptions?.length === 0 ? (
                      <MenuItem disabled sx={{ justifyContent: 'center', fontStyle: 'italic' }}>
                        No options
                      </MenuItem>
                    ) : (
                      companyNatureOptions?.map((option) => (
                        <MenuItem key={option.id} value={option?.code}>
                          {option.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
                <div className="error-msg-b2ship">
                  {errors?.companyNatures?.message || <>&nbsp;</>}
                </div>
              </>
            )}
          />
        </MDBCol>
        <MDBCol md="4">
          <Label name="IMO unique company and registered owner identification number of the company" bold />
          <Controller
            name="companyImoNumber"
            control={control}
            render={({ field }) => (
              <>
                <Input
                  id="b2.company_imo_number"
                  className="form-input"
                  onBlur={() => handleBlur(field.name)}
                  autoFocus={false}
                  value={field.value}
                  onChange={field.onChange}
                />
                <div className="error-msg-b2ship">
                  {"" || <>&nbsp;</>}
                </div>
              </>
            )}
          />
        </MDBCol>
      </MDBRow>
      <MDBRow className="align-items-center mb-3">
        <MDBCol md="4">
          <Label name="Country of registration of the company" bold />
          <Controller
            name="countryOfRegistration"
            control={control}
            render={({ field }) => (
              <Input
                id="b2.country_of_registration"
                className="form-input"
                onBlur={() => handleBlur(field.name)}
                autoFocus={false}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </MDBCol>
        <MDBCol md="4">
          <Label name="Company tax number (voluntary)" bold />
          <Controller
            name="taxNumber"
            control={control}
            render={({ field }) => (
              <Input
                id="b2.tax_number"
                className="form-input"
                onBlur={() => handleBlur(field.name)}
                autoFocus={false}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </MDBCol>
        <MDBCol md="4">
          <Label name="Contact person" bold />
          <Controller
            name="contactPerson"
            control={control}
            render={({ field }) => (
              <Input
                id="b2.contact_person"
                className="form-input"
                onBlur={() => handleBlur(field.name)}
                autoFocus={false}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </MDBCol>
      </MDBRow>
      <MDBRow className="align-items-center mb-3">
        <MDBCol md="4">
          <Label name="Business address" bold />
          <Controller
            name="businessAddress"
            control={control}
            render={({ field }) => (
              <Input
                id="b2.business_address"
                className="form-input"
                onBlur={() => handleBlur(field.name)}
                autoFocus={false}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </MDBCol>
        <MDBCol md="4">
          <Label name="City" bold />
          <Controller
            name="city"
            control={control}
            render={({ field }) => (
              <Input
                id="b2.city"
                className="form-input"
                onBlur={() => handleBlur(field.name)}
                autoFocus={false}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </MDBCol>
        <MDBCol md="4">
          <Label name="State/Province/Region" bold />
          <Controller
            name="stateProvinceRegion"
            control={control}
            render={({ field }) => (
              <Input
                id="b2.state_province"
                className="form-input"
                onBlur={() => handleBlur(field.name)}
                autoFocus={false}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </MDBCol>
      </MDBRow>
      <MDBRow className="align-items-center mb-3">
        <MDBCol md="4">
          <Label name="Postcode/ZIP" bold />
          <Controller
            name="zipCode"
            control={control}
            render={({ field }) => (
              <Input
                id="b2.zip_code"
                className="form-input"
                onBlur={() => handleBlur(field.name)}
                autoFocus={false}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </MDBCol>
        <MDBCol md="4">
          	<Label name="Telephone number" bold />
					<Controller
						name="businessTelephone"
						control={control}
						defaultValue={b2CompanyInfo?.businessTelephone ?? ""}
						rules={{
							validate: (value) => {
								if (!value) return true; // allow empty
								return /^\+?[0-9]{7,15}$/.test(value) ? true : "Enter a valid telephone number (7–15 digits, optional +).";
							},
						}}
						render={({ field }) => (
							<>
								<Input
									id="telephone"
									className="form-input"
									onBlur={() => handleBlur(field.name)}
									autoFocus={false}
									maxLength={16} // enough for + and 15 digits
									value={field.value}
									onChange={field.onChange}
								/>
								<div className="error-msg-b1ship">{errors?.businessTelephone?.message || <>&nbsp;</>}</div>
							</>
						)}
					/>
        </MDBCol>
        <MDBCol md="4">
         <Label name="Email address" bold />
					<Controller
						name="businessEmail"
						control={control}
						defaultValue={b2CompanyInfo?.businessEmail ?? ""}
						rules={{
							validate: (value) => {
								if (!value) return true; // allow empty
								return /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(value) ? true : "Please enter a valid email address.";
							},
						}}
						render={({ field }) => (
							<>
								<Input
									id="email"
									className="form-input"
									onBlur={() => handleBlur(field.name)}
									autoFocus={false}
									maxLength={255}
									value={field.value}
									onChange={field.onChange}
								/>
								<div className="error-msg-b1ship">{errors?.businessEmail?.message || <>&nbsp;</>}</div>
							</>
						)}
					/>
        </MDBCol>
      </MDBRow>
      <MDBRow className="align-items-center mb-3">
        <MDBCol md="4">
          <Label name="Country" bold />
          <Controller
            name="country"
            control={control}
            render={({ field }) => (
              <Input
                id="b2.country"
                className="form-input"
                onBlur={() => handleBlur(field.name)}
                autoFocus={false}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </MDBCol>
      </MDBRow>
      <div style={{ marginTop: "20px", display: "flex", justifyContent: "center" }}>
        <Button
          disabled={!isDirty}
          onClick={async () => {
            const isValid =  await trigger([...REQUIRED_FIELDS_FOR_B2,"businessEmail","businessTelephone"]);;
            if (!isValid) return;

            manualSaveTriggered.current = true;
            await submitData(getValues(), false);
          }}
        >
          Save
        </Button>
      </div>
    </div>
  )
});

export default B2CompanyInformation;